import { Deal } from 'modules/domain/deal/types'
import { useTranslation } from 'react-i18next'
import { useFormik } from 'formik'
import { SharedModalContent, SharedModalInputs } from 'views/components/SharedModal'
import { RequestError } from 'modules/errors'
import { apiClient } from 'modules/utils/httpClient'
import { endpoints } from 'modules/endpoints'
import { ShortFormControls } from 'views/styled/common'
import FormSelectShared from 'views/components/FormSelectShared'
import { useAFormHandler } from 'analytics/hooks'
import { FC, useState } from 'react'
import { refetchFunc } from 'modules/domain/common/hooks'
import { changeErrorsDotsToPipes } from 'modules/utils/helpers'
import { ResponsiveModal } from 'views/components/ResponsiveModal/ResponsiveModal'
import { FormGroup, Input, OptionType, RadioItem } from '@agro-club/agroclub-shared'
import { DealRailwayData, DealRailwayDataDTO } from '../types'
import { labelTarif, rubPerTon } from 'modules/utils/numbers/formatTarif'
import { FormRadio } from 'views/components/form/FormRadio'
import { FormStorageControl } from 'views/pages/Elevators/FormStorageControl'
import { Elevator } from 'modules/domain/warehouses/types'
import { SharedModalSecondContent, SharedModalSubTitle } from 'views/components/SharedModal/styled'

type Props = {
  deal: Deal
  railwayData?: DealRailwayData
  refetch: refetchFunc
  onClose: () => void
}
export const DealRailwayModal: FC<Props> = ({ deal, railwayData, onClose, refetch }) => {
  const { formProgress, formHandler } = useAFormHandler('editDeal', { id: deal.id })
  const { t } = useTranslation(['warehouses'])
  const [selectedElevator, setSelectedElevator] = useState<Elevator | null>(null)

  const formik = useFormik<DealRailwayDataDTO>({
    initialValues: {
      elevator: railwayData ? railwayData.elevator.id.toString() : null,
      from_station: railwayData ? railwayData.from_station.id.toString() : null,
      to_station: railwayData ? railwayData.to_station.id.toString() : null,
      farmer_transit_costs: railwayData ? railwayData.farmer_transit_costs : null,
      acceptance_costs: railwayData ? railwayData.acceptance_costs : null,
      storage_costs: railwayData ? railwayData.storage_costs : null,
      mechanical_railcar_loading_costs: railwayData ? railwayData.mechanical_railcar_loading_costs : null,
      documents_costs: railwayData ? railwayData.documents_costs : null,
      other_costs: railwayData ? railwayData.other_costs : null,
      expedite_costs: railwayData ? railwayData.expedite_costs : null,
      storage_days: railwayData ? railwayData.storage_days : null,
      storage_costs_unit: railwayData ? railwayData.storage_costs_unit : 'per_day',
      rate: railwayData ? railwayData.rate : null,
      is_composite_costs: railwayData ? railwayData.is_composite_costs : false,
      composite_costs: railwayData ? railwayData.composite_costs : null,
    },
    enableReinitialize: true,
    onSubmit: formHandler(
      async () => {
        if (railwayData) {
          await apiClient.put(endpoints.dealRailWayData(deal.id, railwayData.id.toString()), formik.values)
        } else {
          await apiClient.post(endpoints.dealRailWayData(deal.id), formik.values)
        }
      },
      {
        onSuccess: async () => {
          await refetch()
          onClose()
        },
        onError: (err) => {
          const error = err as RequestError
          let { errors } = error
          errors = changeErrorsDotsToPipes(errors)
          formik.setErrors(errors)
        },
      },
    ),
  })

  const elevatorOnChange = (option: OptionType) => {
    const prevElevator = railwayData ? railwayData.elevator.id : null
    const elevator = option.dto as Elevator
    setSelectedElevator(elevator)
    if (prevElevator == option.value) {
      return
    }
    const costs = elevator.elevator_costs[0]
    Object.keys(formik.values).forEach((key) => {
      if (costs[key] !== undefined) {
        formik.setFieldValue(key, costs[key])
      }
    })
    if (elevator.railway_station) {
      formik.setFieldValue('from_station', elevator.railway_station.id)
    }
  }

  return (
    <ResponsiveModal
      onConfirm={() => formik.submitForm()}
      id="railwayModal"
      confirmButtonText={t('common:save')}
      disabled={!formik.dirty}
      title={t('railwayLogistics')}
      progress={formProgress}
      onClose={onClose}
      size="medium"
    >
      <SharedModalContent>
        <SharedModalInputs>
          <SharedModalSecondContent>
            <SharedModalSubTitle>{t('allElevatorCosts')}</SharedModalSubTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <FormSelectShared
                label={t('elevator')}
                formik={formik}
                fieldName="elevator"
                getLabel={(el) => el.title}
                endpoint={endpoints.elevators()}
                searchField="search"
                placeholder=""
                onSelectedOptionLoaded={elevatorOnChange}
              />
              <FormGroup label={t('railwayStation')}>
                <Input
                  value={
                    selectedElevator?.railway_station
                      ? `${selectedElevator?.railway_station.title}(${selectedElevator?.railway_station.station_code})`
                      : ''
                  }
                  disabled
                />
              </FormGroup>
            </div>

            <FormRadio label={t('costs_type')} formik={formik} field="is_composite_costs">
              <RadioItem value={false} label={t('costs_types.detailed')} />
              <RadioItem value={true} label={t('costs_types.composite')} />
            </FormRadio>
            {formik.values.is_composite_costs ? (
              <>
                <FormGroup label={rubPerTon(t('elevator_costs.composite_costs'))} error={formik.errors.composite_costs}>
                  <Input
                    {...formik.getFieldProps('composite_costs')}
                    invalid={!!formik.errors.composite_costs}
                    type="number"
                  />
                </FormGroup>
              </>
            ) : (
              <>
                <ShortFormControls>
                  <FormGroup
                    label={rubPerTon(t('elevator_costs_short.acceptance_costs'))}
                    error={formik.errors.acceptance_costs}
                  >
                    <Input
                      {...formik.getFieldProps('acceptance_costs')}
                      invalid={!!formik.errors.acceptance_costs}
                      type="number"
                    />
                  </FormGroup>
                </ShortFormControls>
                <ShortFormControls width={530}>
                  <FormStorageControl formik={formik} />
                </ShortFormControls>
                <ShortFormControls>
                  <FormGroup label={t('elevator_costs_short.storage_days')} error={formik.errors.storage_days}>
                    <Input
                      {...formik.getFieldProps('storage_days')}
                      invalid={!!formik.errors.storage_days}
                      type="number"
                    />
                  </FormGroup>
                  <FormGroup
                    label={rubPerTon(t('elevator_costs_short.mechanical_railcar_loading_costs'))}
                    error={formik.errors.mechanical_railcar_loading_costs}
                  >
                    <Input
                      {...formik.getFieldProps('mechanical_railcar_loading_costs')}
                      invalid={!!formik.errors.mechanical_railcar_loading_costs}
                      type="number"
                    />
                  </FormGroup>
                  <FormGroup
                    label={rubPerTon(t('elevator_costs_short.expedite_costs'))}
                    error={formik.errors.expedite_costs}
                  >
                    <Input
                      {...formik.getFieldProps('expedite_costs')}
                      invalid={!!formik.errors.expedite_costs}
                      type="number"
                    />
                  </FormGroup>
                  <FormGroup
                    label={rubPerTon(t('elevator_costs_short.documents_costs'))}
                    error={formik.errors.documents_costs}
                  >
                    <Input
                      {...formik.getFieldProps('documents_costs')}
                      invalid={!!formik.errors.documents_costs}
                      type="number"
                    />
                  </FormGroup>
                  <FormGroup label={rubPerTon(t('elevator_costs_short.other_costs'))} error={formik.errors.other_costs}>
                    <Input
                      {...formik.getFieldProps('other_costs')}
                      invalid={!!formik.errors.other_costs}
                      type="number"
                    />
                  </FormGroup>
                </ShortFormControls>
              </>
            )}
          </SharedModalSecondContent>
          <SharedModalSecondContent>
            <SharedModalSubTitle>{t('railwayLogistics')}</SharedModalSubTitle>
            <ShortFormControls>
              <FormGroup
                label={labelTarif(t('elevator_costs_short.farmer_transit_costs'))}
                error={formik.errors.farmer_transit_costs}
              >
                <Input
                  {...formik.getFieldProps('farmer_transit_costs')}
                  invalid={!!formik.errors.farmer_transit_costs}
                  type="number"
                />
              </FormGroup>
            </ShortFormControls>
            <FormSelectShared
              label={t('fromStation')}
              formik={formik}
              fieldName="from_station"
              getLabel={(station) => `${station.title} (${station.station_code})`}
              endpoint={endpoints.LogisticsKR.railwayStations()}
              searchField="search"
              placeholder=""
            />

            <FormSelectShared
              label={t('toStation')}
              formik={formik}
              fieldName="to_station"
              getLabel={(station) => `${station.title} (${station.station_code})`}
              endpoint={endpoints.LogisticsKR.railwayStations()}
              searchField="search"
              placeholder=""
            />
            <FormGroup label={t('rate')} error={formik.errors.rate}>
              <Input {...formik.getFieldProps('rate')} invalid={!!formik.errors.rate} type="number" />
            </FormGroup>
          </SharedModalSecondContent>
        </SharedModalInputs>
      </SharedModalContent>
    </ResponsiveModal>
  )
}
